/**
 * Utility to generate unique component names, appending (2), (3), etc. when duplicates exist.
 * Also provides color normalization for Power (green) vs Lighting (gold) distinction.
 * Used across key extraction, autocount, and manual component creation flows.
 */

// Canonical colors: Power = green, Lighting = gold
export const POWER_COLOR = 'rgba(34, 197, 94, 0.6)';
export const LIGHTING_COLOR = 'rgba(234, 179, 8, 0.6)';

/**
 * Normalizes component colors for backwards compatibility.
 * Non-Lighting components are forced to green (power), Lighting stays gold.
 * Call this when loading project_key from the database to fix old color assignments.
 */
export function normalizeComponentColors<T extends { color: string; customColor?: boolean; componentType?: string; symbolType?: string; name?: string; symbol?: string; description?: string; referenceImage?: string; isStandardClass?: boolean; pages?: any[]; totalCount?: number }>(
  components: T[]
): T[] {
  return components.map(comp => {
    if (comp.customColor) return comp;
    const isLighting =
      comp.componentType === 'Lighting' ||
      comp.symbolType === 'Lighting';
    const correctColor = isLighting ? LIGHTING_COLOR : POWER_COLOR;
    if (comp.color !== correctColor) {
      return { ...comp, color: correctColor };
    }
    return comp;
  });
}

/**
 * Returns a unique component name by appending (2), (3), etc. if the name already exists.
 * Comparison is case-insensitive to avoid "Duplex Receptacle" vs "duplex receptacle" collisions.
 *
 * @param desiredName - The name we want to use
 * @param existingNames - Array of names already taken
 * @returns A unique name, possibly with a suffix like " (2)"
 */
export function getUniqueComponentName(
  desiredName: string,
  existingNames: string[]
): string {
  const lowerNames = existingNames.map(n => n.toLowerCase());

  if (!lowerNames.includes(desiredName.toLowerCase())) {
    return desiredName;
  }

  let counter = 2;
  while (lowerNames.includes(`${desiredName} (${counter})`.toLowerCase())) {
    counter++;
  }
  return `${desiredName} (${counter})`;
}

/**
 * Deduplicates an array of items that have a `name` property, appending suffixes to duplicates.
 * Preserves the original order. The first occurrence keeps its original name.
 *
 * @param items - Array of objects with at least a `name` property
 * @returns New array with unique names
 */
export function deduplicateComponentNames<T extends { name: string }>(
  items: T[]
): T[] {
  const seenNames: string[] = [];

  return items.map(item => {
    const uniqueName = getUniqueComponentName(item.name, seenNames);
    seenNames.push(uniqueName);
    if (uniqueName !== item.name) {
      return { ...item, name: uniqueName };
    }
    return item;
  });
}
